import React from 'react';
import { Modal, Form, Input, Button, Typography, message } from 'antd';
import { useTranslation } from '@refinedev/core';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import useAPI from '@/api/useApi';
import { endPoints } from '@/api/configs';

const { TextArea } = Input;
const { Title } = Typography;

interface ReviewPostModalProps {
  reqId?: string;
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

export const ReviewPostModal: React.FC<ReviewPostModalProps> = ({
  open,
  onClose,
  reqId,
  refetch,
}) => {
  const { translate } = useTranslation();
  const [form] = Form.useForm();
  const { usePost } = useAPI();
  const { mutateAsync: approveRequest, isLoading: approveLoading } = usePost(
    endPoints.approveRequest(String(reqId))
  );
  const { mutateAsync: rejectRequest, isLoading: rejectLoading } = usePost(
    endPoints.rejectRequest(String(reqId))
  );
  const handleSubmit = async (action: 'approve' | 'reject') => {
    if (action === 'approve') {
      approveRequest(
        {},
        {
          onSuccess: () => {
            message.success(translate('requests-user.success.approve'));
            refetch();
            onClose();
          },
          onError: (error) => {
            message.error(error.message);
          },
        }
      );
    } else {
      const feedback = form.getFieldValue('feedback');
      if (!feedback) {
        message.error(translate('requests-user.error.feedback-required'));
        return;
      }
      rejectRequest(
        { feedback },
        {
          onSuccess: () => {
            message.success(translate('requests-user.success.reject'));
            refetch();
            onClose();
          },
          onError: (error) => {
            message.error(error.message);
          },
        }
      );
    }
  };
  if (!reqId) {
    return null;
  }
  return (
    <Modal
      title={
        <Title level={4} className="mb-0">
          {translate('buttons.review')}
        </Title>
      }
      open={open}
      onCancel={onClose}
      width={700}
      footer={[
        <Button
          key="reject"
          danger
          icon={<CloseOutlined />}
          onClick={() => handleSubmit('reject')}
          loading={rejectLoading}
        >
          {translate('buttons.reject')}
        </Button>,
        <Button
          key="approve"
          className="bg-green-500 border-green-500"
          icon={<CheckOutlined />}
          onClick={() => handleSubmit('approve')}
          loading={approveLoading}
        >
          {translate('buttons.approve')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="feedback"
          label={translate('users.feedback')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder={translate('users.feedbackPlaceholder')}
            className="mt-2"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
